import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { UsageKPI, UsageSummary, UsageWindow } from "../types"

import { FileSpreadsheet, Inbox, PowerOff, RefreshCw } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"

import { exportUsageSummaryToXlsx } from "../export-xlsx"
import { useUsageSummary } from "../hooks/use-slide-sense-usage"
import { SlideUsageChart } from "./usage/SlideUsageChart"
import { SlideUsageKPIs } from "./usage/SlideUsageKPIs"
import { TopNTable } from "./usage/TopNTable"
import { UnusedItemsReport } from "./usage/UnusedItemsReport"
import { UsageErrorState } from "./usage/UsageErrorState"

function kpiIsEmpty(kpi: UsageKPI | undefined): boolean {
  if (!kpi) return true
  return (
    kpi.active_users === 0 &&
    kpi.total_exports === 0 &&
    kpi.total_presentations === 0 &&
    kpi.presentation_sessions === 0 &&
    kpi.distinct_slides_used === 0
  )
}

function statusFromError(error: unknown): number | undefined {
  if (error && typeof error === "object" && "response" in error) {
    const resp = (error as { response?: { status?: number } }).response
    return resp?.status
  }
  return undefined
}

export function SlideSenseUsageDashboard() {
  const { t } = useTranslation()
  const [selectedWindow, setWindow] = useState<UsageWindow>("30d")
  const { data, isLoading, isError, error, refetch, isFetching } = useUsageSummary(selectedWindow)

  const errorStatus = isError ? statusFromError(error) : undefined
  const showDisabled = errorStatus === 503

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-6xl p-6">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t("slideSense.usage.title")}</h1>
            <p className="text-muted-foreground text-sm">{t("slideSense.usage.description")}</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={selectedWindow} onValueChange={(v) => setWindow(v as UsageWindow)}>
              <SelectTrigger aria-label={t("slideSense.usage.window.label")} className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">{t("slideSense.usage.window.last7")}</SelectItem>
                <SelectItem value="30d">{t("slideSense.usage.window.last30")}</SelectItem>
                <SelectItem value="90d">{t("slideSense.usage.window.last90")}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              aria-label={t("slideSense.usage.refresh")}
              data-testid="usage-refresh"
              disabled={isFetching}
              size="sm"
              variant="outline"
              onClick={() => refetch()}
            >
              <RefreshCw
                aria-hidden
                className={`mr-2 h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`}
              />
              {isFetching ? t("slideSense.usage.refreshing") : t("slideSense.usage.refresh")}
            </Button>
            <Button
              aria-label={t("slideSense.usage.exportExcel")}
              data-testid="usage-export-excel"
              disabled={!data}
              size="sm"
              variant="outline"
              onClick={() => {
                if (!data) return
                exportUsageSummaryToXlsx(data, selectedWindow, {
                  columns: {
                    metric: t("slideSense.usage.export.col.metric"),
                    value: t("slideSense.usage.export.col.value"),
                    date: t("slideSense.usage.export.col.date"),
                    uses: t("slideSense.usage.col.uses"),
                    exports: t("slideSense.usage.col.exports"),
                    slideId: t("slideSense.usage.export.col.slideId"),
                    slideTitle: t("slideSense.usage.col.slide"),
                    deckId: t("slideSense.usage.export.col.deckId"),
                    deckName: t("slideSense.usage.col.deck"),
                    userId: t("slideSense.usage.export.col.userId"),
                    userEmail: t("slideSense.usage.col.user"),
                    events: t("slideSense.usage.col.events"),
                    presentations: t("slideSense.usage.col.presentations"),
                    query: t("slideSense.usage.col.query"),
                    count: t("slideSense.usage.col.count"),
                    avgResults: t("slideSense.usage.col.avgResults"),
                  },
                  kpiRows: {
                    activeUsers: t("slideSense.usage.kpi.activeUsers"),
                    totalExports: t("slideSense.usage.kpi.totalExports"),
                    totalPresentations: t("slideSense.usage.kpi.totalPresentations"),
                    presentationSessions: t("slideSense.usage.kpi.presentationSessions"),
                    distinctSlidesUsed: t("slideSense.usage.kpi.distinctSlidesUsed"),
                  },
                })
              }}
            >
              <FileSpreadsheet aria-hidden className="mr-2 h-3.5 w-3.5" />
              {t("slideSense.usage.exportExcel")}
            </Button>
          </div>
        </header>

        {isLoading && <LoadingSkeleton />}

        {showDisabled && <DisabledState />}

        {isError && !showDisabled && (
          <UsageErrorState isRetrying={isFetching} onRetry={() => refetch()} />
        )}

        {data && !isError && <Body data={data} />}
      </div>
    </div>
  )
}

function Body({ data }: { data: UsageSummary }) {
  const { t } = useTranslation()
  const empty = kpiIsEmpty(data.kpi)

  return (
    <>
      <SlideUsageKPIs kpi={data.kpi} />
      {empty ? (
        <NoActivityState />
      ) : (
        <>
          <SlideUsageChart data={data.daily} />
          <div className="grid gap-6 md:grid-cols-2">
            <TopNTable
              emptyLabel={t("slideSense.usage.empty")}
              rows={data.top_slides}
              title={t("slideSense.usage.topSlides")}
              columns={[
                {
                  key: "slide_id",
                  label: t("slideSense.usage.col.slide"),
                  render: (row) => (
                    <div className="min-w-0">
                      <div className="truncate font-medium">
                        {row.slide_title ?? row.ai_title ?? row.slide_id}
                      </div>
                      {row.deck_name ? (
                        <div className="text-muted-foreground truncate text-xs">
                          {row.deck_name}
                        </div>
                      ) : null}
                    </div>
                  ),
                },
                { key: "uses", label: t("slideSense.usage.col.uses"), align: "right" },
              ]}
            />
            <TopNTable
              emptyLabel={t("slideSense.usage.empty")}
              rows={data.top_users}
              title={t("slideSense.usage.topUsers")}
              columns={[
                {
                  key: "user_email",
                  label: t("slideSense.usage.col.user"),
                  render: (row) => row.user_email ?? row.user_id,
                },
                { key: "events", label: t("slideSense.usage.col.events"), align: "right" },
                { key: "exports", label: t("slideSense.usage.col.exports"), align: "right" },
                {
                  key: "presentations",
                  label: t("slideSense.usage.col.presentations"),
                  align: "right",
                },
              ]}
            />
            <TopNTable
              emptyLabel={t("slideSense.usage.empty")}
              rows={data.top_decks}
              title={t("slideSense.usage.topDecks")}
              columns={[
                {
                  key: "deck_id",
                  label: t("slideSense.usage.col.deck"),
                  render: (row) => (
                    <span className="truncate font-medium">
                      {row.deck_name ?? row.name ?? row.deck_id}
                    </span>
                  ),
                },
                { key: "uses", label: t("slideSense.usage.col.uses"), align: "right" },
              ]}
            />
            <TopNTable
              emptyLabel={t("slideSense.usage.empty")}
              rows={data.top_searches}
              title={t("slideSense.usage.topSearches")}
              columns={[
                { key: "query", label: t("slideSense.usage.col.query") },
                { key: "count", label: t("slideSense.usage.col.count"), align: "right" },
                { key: "avg_results", label: t("slideSense.usage.col.avgResults"), align: "right" },
              ]}
            />
          </div>

          <UnusedItemsReport />
        </>
      )}
    </>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-card rounded-lg border p-5 shadow-sm">
            <Skeleton className="mb-3 h-4 w-24" />
            <Skeleton className="h-8 w-16" />
          </div>
        ))}
      </div>
      <Skeleton className="h-[280px] w-full rounded-lg" />
      <div className="grid gap-6 md:grid-cols-2">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-[200px] w-full rounded-lg" />
        ))}
      </div>
    </div>
  )
}

function NoActivityState() {
  const { t } = useTranslation()
  return (
    <div className="bg-card rounded-lg border border-dashed" role="status">
      <EmptyState
        description={t("slideSense.usage.noActivityDescription")}
        icon={Inbox}
        title={t("slideSense.usage.noActivityTitle")}
      />
    </div>
  )
}

function DisabledState() {
  const { t } = useTranslation()
  return (
    <div className="bg-card rounded-lg border border-dashed" role="status">
      <EmptyState
        description={t("slideSense.usage.disabledDescription")}
        icon={PowerOff}
        title={t("slideSense.usage.disabledTitle")}
      />
    </div>
  )
}
